import { useState } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { toast } from 'sonner';

export default function ContactPage() {
  const tenant = useTenant();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Por favor, completa todos los campos');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Por favor, introduce un email válido');
      return;
    }

    setIsSubmitting(true);

    try {
      // TODO: Integrar con el backend cuando esté listo
      // const response = await fetch('/api/contact', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(formData)
      // });

      // Simulación temporal
      await new Promise(resolve => setTimeout(resolve, 1000));

      toast.success('¡Gracias! Hemos recibido tu mensaje.');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      toast.error('No se pudo enviar el mensaje. Inténtalo de nuevo más tarde.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen py-12 px-4"
      style={{
        background: tenant.theme.colors.gradients.main,
        fontFamily: tenant.theme.fonts.body.family
      }}
    >
      <div className="max-w-5xl mx-auto">
        <div
          className="p-8 rounded-lg shadow-lg"
          style={{
            background: 'rgba(17, 24, 39, 0.95)',
            border: '1px solid rgba(51, 65, 85, 0.5)'
          }}
        >
          <h1
            className="text-4xl font-bold mb-6"
            style={{
              fontFamily: tenant.theme.fonts.primary.family,
              color: tenant.theme.colors.accent
            }}
          >
            Contacto
          </h1>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Formulario */}
            <div
              className="p-6 rounded-lg"
              style={{
                background: 'rgba(11, 18, 32, 0.8)',
                border: '1px solid rgba(51, 65, 85, 0.5)'
              }}
            >
              <p className="text-gray-300 mb-6">
                ¿Tienes dudas, sugerencias o quieres reportar un problema? Escríbenos y te
                responderemos lo antes posible.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label
                    className="block mb-2 font-semibold"
                    style={{ color: tenant.theme.colors.primary }}
                  >
                    Nombre
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                    style={{
                      background: 'rgba(15, 23, 42, 0.9)',
                      borderColor: 'rgba(51, 65, 85, 0.8)',
                      color: '#fff'
                    }}
                    required
                  />
                </div>

                <div>
                  <label
                    className="block mb-2 font-semibold"
                    style={{ color: tenant.theme.colors.primary }}
                  >
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                    style={{
                      background: 'rgba(15, 23, 42, 0.9)',
                      borderColor: 'rgba(51, 65, 85, 0.8)',
                      color: '#fff'
                    }}
                    required
                  />
                </div>

                <div>
                  <label
                    className="block mb-2 font-semibold"
                    style={{ color: tenant.theme.colors.primary }}
                  >
                    Mensaje
                  </label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={5}
                    className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 resize-vertical"
                    style={{
                      background: 'rgba(15, 23, 42, 0.9)',
                      borderColor: 'rgba(51, 65, 85, 0.8)',
                      color: '#fff'
                    }}
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 px-6 rounded-lg font-bold transition-opacity hover:opacity-90 disabled:opacity-50"
                  style={{
                    background: tenant.theme.colors.accent,
                    color: tenant.slug === 'kids' ? '#fff' : '#301934'
                  }}
                >
                  {isSubmitting ? 'Enviando...' : 'Enviar'}
                </button>
              </form>
            </div>

            {/* Información adicional */}
            <div
              className="p-6 rounded-lg space-y-6"
              style={{
                background: 'rgba(11, 18, 32, 0.8)',
                border: '1px solid rgba(51, 65, 85, 0.5)'
              }}
            >
              <div>
                <h2
                  className="text-2xl font-semibold mb-3"
                  style={{ color: tenant.theme.colors.accent }}
                >
                  También puedes
                </h2>
                <p className="text-gray-300">
                  Escribirnos por email a{' '}
                  <a
                    href="mailto:soporte@neureka.es"
                    className="underline hover:no-underline"
                    style={{ color: tenant.theme.colors.primary }}
                  >
                    soporte@neureka.es
                  </a>
                </p>
              </div>

              <div>
                <p className="text-gray-300">
                  Consulta nuestras{' '}
                  <a
                    href="/privacy"
                    className="underline hover:no-underline"
                    style={{ color: tenant.theme.colors.primary }}
                  >
                    políticas de privacidad
                  </a>
                  {' '}y{' '}
                  <a
                    href="/terms"
                    className="underline hover:no-underline"
                    style={{ color: tenant.theme.colors.primary }}
                  >
                    términos
                  </a>.
                </p>
              </div>

              <div
                className="p-4 rounded-lg mt-6"
                style={{ background: 'rgba(15, 23, 42, 0.6)' }}
              >
                <h3
                  className="font-semibold mb-2"
                  style={{ color: tenant.theme.colors.accent }}
                >
                  💡 Consejo
                </h3>
                <p className="text-sm text-gray-300">
                  Para reportes de bugs o problemas técnicos, incluye detalles como el navegador
                  que usas, el juego en el que ocurrió el error y los pasos para reproducirlo.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
